/**
 * My Assignments Page
 *
 * Shows all of the crew member's assignments with full event details.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  respondToAssignment,
  type Assignment,
  type AssignmentStatus,
} from '../../api/assignments';
import { getErrorMessage } from '../../api/client';
import { useMyAssignments, useInvalidateMyAssignments } from '../../hooks/queries';
import { useInvalidateCrewDashboard } from '../../hooks/queries/useCrewDashboard';
import {
  startOfToday,
  compareByDateThenStart,
  formatLongDate,
  formatTime,
  formatTimeWithZone,
} from '../../utils/dateFormatting';
import { Modal } from '../../components/ui';

type TabType = 'upcoming' | 'past' | 'all';

const statusConfig: Record<AssignmentStatus, { label: string; className: string }> = {
  ASSIGNED: {
    label: 'Pending Response',
    className: 'bg-blue-100 text-blue-700',
  },
  CONFIRMED: {
    label: 'Confirmed',
    className: 'bg-green-100 text-green-700',
  },
  DECLINED: {
    label: 'Declined',
    className: 'bg-red-100 text-red-700',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'bg-gray-100 text-gray-700',
  },
};

export default function MyAssignmentsPage() {
  const { assignments, loading, error } = useMyAssignments();
  const invalidateMyAssignments = useInvalidateMyAssignments();
  const invalidateCrewDashboard = useInvalidateCrewDashboard();
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Decline modal state
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [decliningAssignment, setDecliningAssignment] = useState<Assignment | null>(null);

  // Events happening today count as upcoming for the whole day, not past.
  const todayStart = startOfToday();

  const filteredAssignments = assignments.filter((a) => {
    if (!a.event) return false;
    const eventDate = new Date(a.event.eventDate);

    const isVisibleToCrew =
      a.event.status === 'PUBLISHED' || a.event.assignmentsConfirmed;
    switch (activeTab) {
      case 'upcoming':
        return eventDate >= todayStart && isVisibleToCrew;
      case 'past':
        return eventDate < todayStart;
      case 'all':
      default:
        return true;
    }
  });

  // Past tab is reverse-chronological; upcoming/all are chronological.
  const sortedAssignments = [...filteredAssignments].sort((a, b) => {
    if (!a.event || !b.event) return 0;
    const cmp = compareByDateThenStart(a.event, b.event);
    return activeTab === 'past' ? -cmp : cmp;
  });

  const handleConfirm = async (assignment: Assignment) => {
    try {
      await respondToAssignment(assignment.id, { status: 'CONFIRMED' });
      invalidateMyAssignments();
      toast.success('Assignment confirmed');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleOpenDecline = (assignment: Assignment) => {
    setDecliningAssignment(assignment);
    setDeclineModalOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Assignments</h1>
          <p className="text-gray-600 text-sm mt-1">
            View and manage your event assignments
          </p>
        </div>
        {/* Discoverability: the feed URLs live on Profile, but this is where
            crew actually look at their schedule. */}
        <Link
          to="/crew/profile"
          className="flex-shrink-0 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
        >
          Add to calendar
        </Link>
      </div>

      {/* Load error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {[
          { key: 'upcoming', label: 'Upcoming' },
          { key: 'past', label: 'Past' },
          { key: 'all', label: 'All' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as TabType)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Assignments List */}
      {sortedAssignments.length > 0 ? (
        <div className="space-y-4">
          {sortedAssignments.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              isExpanded={expandedId === assignment.id}
              onToggle={() => setExpandedId(expandedId === assignment.id ? null : assignment.id)}
              onConfirm={() => handleConfirm(assignment)}
              onDecline={() => handleOpenDecline(assignment)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500">
            {activeTab === 'upcoming'
              ? 'No upcoming assignments'
              : activeTab === 'past'
              ? 'No past assignments'
              : 'No assignments found'}
          </p>
        </div>
      )}

      {/* Decline Modal */}
      {decliningAssignment && (
        <DeclineModal
          isOpen={declineModalOpen}
          onClose={() => {
            setDeclineModalOpen(false);
            setDecliningAssignment(null);
          }}
          assignment={decliningAssignment}
          onDeclined={() => {
            // A decline can also set availability (org setting), so refresh
            // the availability queries too, not just the assignment list.
            invalidateCrewDashboard();
            toast.success('Assignment declined');
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Assignment Card
// =============================================================================

interface AssignmentCardProps {
  assignment: Assignment;
  isExpanded: boolean;
  onToggle: () => void;
  onConfirm: () => void;
  onDecline: () => void;
}

function AssignmentCard({
  assignment,
  isExpanded,
  onToggle,
  onConfirm,
  onDecline,
}: AssignmentCardProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const event = assignment.event;
  const role = assignment.role;

  if (!event) return null;

  const eventDate = new Date(event.eventDate);
  const isPast = eventDate < new Date();
  const isVisibleToCrew = event.status === 'PUBLISHED' || event.assignmentsConfirmed;
  const needsResponse = assignment.status === 'ASSIGNED' && isVisibleToCrew && !isPast;

  const formattedDate = formatLongDate(event.eventDate);

  const formattedCallTime = event.callTime
    ? formatTimeWithZone(event.callTime)
    : null;

  const formattedStartTime = event.startTime
    ? formatTimeWithZone(event.startTime)
    : null;

  const formattedEndTime = event.endTime
    ? formatTimeWithZone(event.endTime)
    : null;

  const handleConfirmClick = async () => {
    setIsConfirming(true);
    await onConfirm();
    setIsConfirming(false);
  };

  const status = statusConfig[assignment.status];

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border ${
        needsResponse ? 'border-2 border-blue-300' : 'border-gray-200'
      }`}
    >
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-start justify-between"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-gray-900">{event.name}</h3>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${status.className}`}>
              {status.label}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {event.venue} • {formattedDate}
          </p>
          {formattedCallTime && (
            <p className="text-sm font-medium text-primary-600 mt-1">
              Crew Call: {formattedCallTime}
            </p>
          )}
          <p className="text-sm text-gray-600 mt-1">{role?.roleName || 'TBD'}</p>
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
          {/* Event Details */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Crew Call</p>
              <p className="font-medium">{formattedCallTime || 'TBD'}</p>
            </div>
            <div>
              <p className="text-gray-500">Start Time</p>
              <p className="font-medium">{formattedStartTime || 'TBD'}</p>
            </div>
            <div>
              <p className="text-gray-500">End Time</p>
              <p className="font-medium">{formattedEndTime || 'TBD'}</p>
            </div>
            {event.sport && (
              <div>
                <p className="text-gray-500">Sport</p>
                <p className="font-medium">{event.sport}</p>
              </div>
            )}
            {role?.description && (
              <div className="col-span-2">
                <p className="text-gray-500">Role Description</p>
                <p className="font-medium">{role.description}</p>
              </div>
            )}
          </div>

          {/* Event Notes */}
          {(event.tocInfo || event.engineeringNotes || event.additionalDetails) && (
            <div className="space-y-2 text-sm">
              {event.tocInfo && (
                <div>
                  <p className="text-gray-500">TOC Info</p>
                  <p className="text-gray-700">{event.tocInfo}</p>
                </div>
              )}
              {event.engineeringNotes && (
                <div>
                  <p className="text-gray-500">Engineering Notes</p>
                  <p className="text-gray-700">{event.engineeringNotes}</p>
                </div>
              )}
              {event.additionalDetails && (
                <div>
                  <p className="text-gray-500">Additional Details</p>
                  <p className="text-gray-700">{event.additionalDetails}</p>
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          {event.timelineEntries && event.timelineEntries.length > 0 && (
            <div>
              <p className="text-gray-500 text-sm mb-2">Event Timeline</p>
              <div className="space-y-2">
                {event.timelineEntries
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((entry) => (
                    <div key={entry.id} className="flex items-start text-sm">
                      <span className="font-medium text-gray-900 w-20">
                        {formatTime(entry.time)}
                      </span>
                      <span className="text-gray-700">{entry.label}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Decline Reason (if declined) */}
          {assignment.status === 'DECLINED' && assignment.declineReason && (
            <div className="p-3 bg-red-50 rounded-md text-sm">
              <p className="text-red-700 font-medium">Decline Reason:</p>
              <p className="text-red-600">{assignment.declineReason}</p>
            </div>
          )}

          {/* View full event link */}
          <div className="pt-2 border-t border-gray-100">
            <Link
              to={`/crew/events/${event.id}`}
              className="text-sm text-primary-600 hover:text-primary-500 font-medium"
            >
              View full event details & crew →
            </Link>
          </div>

          {/* Actions */}
          {needsResponse && (
            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleConfirmClick}
                disabled={isConfirming}
                className="flex-1 bg-green-600 text-white text-sm font-medium py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {isConfirming ? 'Confirming...' : 'Confirm Assignment'}
              </button>
              <button
                onClick={onDecline}
                disabled={isConfirming}
                className="flex-1 bg-red-100 text-red-700 text-sm font-medium py-2 rounded hover:bg-red-200 disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Decline Modal
// =============================================================================

interface DeclineModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: Assignment;
  onDeclined: () => void;
}

function DeclineModal({ isOpen, onClose, assignment, onDeclined }: DeclineModalProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDecline = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await respondToAssignment(assignment.id, {
        status: 'DECLINED',
        declineReason: reason.trim() || null,
      });
      onDeclined();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" labelledBy="decline-modal-title">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 id="decline-modal-title" className="text-lg font-semibold text-gray-900">Decline Assignment</h3>
        <p className="text-sm text-gray-500 mt-1">
          {assignment.event?.name} - {assignment.role?.roleName}
        </p>
      </div>

      <div className="px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
          Reason (optional)
        </label>
        <textarea
          id="reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
          placeholder="Let the admin know why you can't work this shift..."
        />
        <p className="text-xs text-gray-400 mt-1">{reason.length}/500 characters</p>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Declining...' : 'Decline Assignment'}
        </button>
      </div>
    </Modal>
  );
}
